import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, Sparkles, Pencil, Copy, Trash2, Search, Send, ImageIcon, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { useWorkspace, useFeature } from '@/contexts/WorkspaceContext';
import { can } from '@/lib/permissions';
import { DEFAULT_CAMPAIGN_CHANNEL } from '@/lib/campaignChannel';
import { mediaHandOff, templateFromMediaPath, type MediaHandOff } from '@/lib/flowLinks';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';
import { useMediaErrorRefresh } from '@/hooks/useMediaErrorRefresh';
import { normalize, selectedText, selectedPhotos, selectedVideos, generationCount } from '@/lib/generatedContent';

// The reusable creative library.
//
// This is the half of the Campaigns page that is about MAKING things. It sits beside the campaign
// list as a tab rather than at its own URL because making and sending are one workflow — but the
// creative is still a separate entity from the campaign, which is what lets one piece of it back
// several sends. Previously the generated copy and images belonged to the single campaign that
// produced them, so running the same material at a second audience meant generating — and paying
// for — all of it again.

interface CampaignMedia {
  id: string;
  name: string;
  type: string;
  createdAt: string;
  contentTypes?: string[];
  generatedContent?: unknown;
}

interface CampaignMediaPanelProps {
  // Deliberately NOT subscription-guarded: it only navigates, and the builder it navigates to is
  // wrapped in SubscriptionGate, so a lapsed workspace is stopped structurally at the destination.
  onCreate: () => void;
  // The page owns the single `useSubscriptionGuard` (and the one `nudge` it renders), so the
  // guard arrives as a prop rather than being called a second time here.
  guard: (fn: () => void) => () => void;
}

export default function CampaignMediaPanel({ onCreate, guard }: CampaignMediaPanelProps) {
  const navigate = useNavigate();
  const { workspaceId, permissions } = useWorkspace();
  const [media, setMedia] = useState<CampaignMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<CampaignMedia | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [starting, setStarting] = useState<string | null>(null);

  const canCreate = can(permissions, 'campaign:create');
  const canDelete = can(permissions, 'campaign:delete');
  const canUpdate = can(permissions, 'campaign:update');
  const showError = useApiErrorToast();
  const hasCustomTemplates = useFeature('templates_custom');
  // The same rule as the builder's own button: into a template for anyone who can make one.
  const handOff = mediaHandOff({
    canManageTemplates: can(permissions, 'templates:manage'),
    hasCustomTemplates,
    canCreateCampaign: canCreate,
  });
  const canReadTemplates = can(permissions, 'templates:read');
  // How many templates were made from the media about to be deleted. They keep working, but lose
  // it as their campaigns' default — said in the confirmation, before the decision, not after.
  const [linkedTemplateCount, setLinkedTemplateCount] = useState(0);

  useEffect(() => {
    if (!deleteTarget || !canReadTemplates) {
      setLinkedTemplateCount(0);
      return;
    }
    let cancelled = false;
    apiFetch<{ pagination: { total: number } }>(
      `/workspaces/${workspaceId}/whatsapp-templates?campaignMediaId=${deleteTarget.id}&limit=1`,
    )
      .then((data) => { if (!cancelled) setLinkedTemplateCount(data.pagination?.total || 0); })
      .catch(() => { if (!cancelled) setLinkedTemplateCount(0); });
    return () => { cancelled = true; };
  }, [deleteTarget, workspaceId, canReadTemplates]);

  // `quiet` is the refetch for thumbnails whose signed URLs expired: the grid stays where it is
  // instead of turning into a spinner, and a failure is not worth a toast.
  const fetchMedia = async (term: string, { quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const params = term ? `?search=${encodeURIComponent(term)}` : '';
      const data = await apiFetch<{ media: CampaignMedia[] }>(
        `/workspaces/${workspaceId}/campaign-media${params}`,
      );
      setMedia(data.media || []);
    } catch (error) {
      if (!quiet) showError(error, 'Failed to load your media.');
    } finally {
      if (!quiet) setLoading(false);
    }
  };
  const refreshThumbnails = useMediaErrorRefresh(() => fetchMedia(search, { quiet: true }));

  useEffect(() => {
    if (!workspaceId) return;
    const debounce = setTimeout(() => fetchMedia(search), 300);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, search]);

  const handleDuplicate = async (item: CampaignMedia) => {
    try {
      const { media: clone } = await apiFetch<{ media: CampaignMedia }>(
        `/workspaces/${workspaceId}/campaign-media/${item.id}/duplicate`,
        { method: 'POST' },
      );
      toast.success('Media duplicated');
      navigate(`/w/${workspaceId}/campaigns/media/${clone.id}`);
    } catch (error) {
      showError(error, 'Failed to duplicate this media.');
    }
  };

  // Opens the template composer pre-filled from this media: its text as the message, its selected
  // photo or video as the header. Nothing to save first — a card changes no selection.
  const handleUseInTemplate = (item: CampaignMedia) => {
    navigate(templateFromMediaPath(workspaceId, item.id));
  };

  // Creates the campaign with this media already chosen, and opens the setup stepper on its first
  // step so the operator can confirm the name (prefilled from the media) and add a description.
  // The fallback for anyone who cannot make a template (see lib/flowLinks).
  const handleUseInCampaign = async (item: CampaignMedia) => {
    setStarting(item.id);
    try {
      const { campaign } = await apiFetch<{ campaign: { id: string } }>(
        `/workspaces/${workspaceId}/campaigns`,
        // The media tab belongs to no channel, so the campaign takes the one that ships.
        {
          method: 'POST',
          body: JSON.stringify({ name: item.name, campaignMediaId: item.id, type: DEFAULT_CAMPAIGN_CHANNEL }),
        },
      );
      navigate(`/w/${workspaceId}/campaigns/${campaign.id}?step=details`);
    } catch (error) {
      showError(error, 'Could not start a campaign from this media.');
    } finally {
      setStarting(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiFetch(`/workspaces/${workspaceId}/campaign-media/${deleteTarget.id}`, { method: 'DELETE' });
      toast.success('Media deleted');
      setDeleteTarget(null);
      fetchMedia(search);
    } catch (error) {
      // The server refuses with a 409 naming the campaigns still using it — that message is far
      // more useful than a generic failure, and describeApiError's 409 branch keeps it as the title.
      showError(error, 'Failed to delete this media.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="relative mb-6 max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search media..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading media…
        </div>
      ) : media.length === 0 ? (
        <Card className="min-h-[240px] flex flex-col items-center justify-center gap-3 p-8 text-center text-muted-foreground">
          <ImageIcon className="h-10 w-10 opacity-50" />
          <p className="text-lg font-medium">
            {search ? 'No media matches that search' : 'No media yet'}
          </p>
          {!search && canCreate && (
            <Button onClick={onCreate}>
              <Sparkles className="mr-2 h-4 w-4" /> Create your first media
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {media.map((item) => (
            <MediaCard
              key={item.id}
              item={item}
              canUpdate={canUpdate}
              canCreate={canCreate}
              canDelete={canDelete}
              starting={starting === item.id}
              handOff={handOff}
              onOpen={() => navigate(`/w/${workspaceId}/campaigns/media/${item.id}`)}
              // Guarded here rather than inside MediaCard, so an unsubscribed click correctly shows
              // the nudge instead of a spinner. These lead to requireActiveSubscription endpoints;
              // Open and the create button do not.
              onDuplicate={guard(() => handleDuplicate(item))}
              onUse={guard(() => (handOff === 'template' ? handleUseInTemplate(item) : handleUseInCampaign(item)))}
              onDelete={() => setDeleteTarget(item)}
              onMediaError={refreshThumbnails}
            />
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this media?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.name}" and everything generated for it will be removed. Campaigns that
              already used it are not affected.
              {linkedTemplateCount > 0 && (
                <>
                  {' '}
                  {linkedTemplateCount === 1 ? 'The template' : `The ${linkedTemplateCount} templates`} made
                  from it will keep working, but new campaigns using{' '}
                  {linkedTemplateCount === 1 ? 'it' : 'them'} will no longer start with this media.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting…</> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface MediaCardProps {
  item: CampaignMedia;
  canUpdate: boolean;
  canCreate: boolean;
  canDelete: boolean;
  starting: boolean;
  /** What the main button does — into a template, or straight into a campaign. */
  handOff: MediaHandOff;
  onOpen: () => void;
  onDuplicate: () => void;
  onUse: () => void;
  onDelete: () => void;
  /** The thumbnail failed to load — most likely its signed URL expired. */
  onMediaError: () => void;
}

function MediaCard({
  item, canUpdate, canCreate, canDelete, starting, handOff, onOpen, onDuplicate, onUse, onDelete, onMediaError,
}: MediaCardProps) {
  // The card shows the FINAL selection, which is also exactly what a campaign gets when it picks
  // this media — so what is on the card is what would be sent.
  const gc = useMemo(() => normalize(item.generatedContent), [item.generatedContent]);
  const options = generationCount(gc);
  const text = selectedText(gc)?.content || '';
  const photo = selectedPhotos(gc)[0];
  // A template is pre-filled from the selection, so a media item with none selected has nothing
  // to carry across yet.
  const hasSelection = Boolean(text) || Boolean(photo) || selectedVideos(gc).length > 0;

  return (
    <Card className="flex flex-col gap-3 overflow-hidden p-0">
      {photo?.url ? (
        <img src={photo.url} alt="" className="aspect-video w-full object-cover" onError={onMediaError} />
      ) : (
        <div className="flex aspect-video w-full items-center justify-center bg-muted text-muted-foreground">
          <ImageIcon className="h-8 w-8 opacity-40" />
        </div>
      )}

      <div className="flex flex-1 flex-col gap-3 p-5 pt-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold leading-tight line-clamp-2">{item.name}</h3>
          {options > 0 && (
            <Badge variant="secondary" className="shrink-0">
              {options} option{options > 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="capitalize">{item.type}</span>
          <span>·</span>
          <span>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ''}</span>
        </div>

        <p className="min-h-[3rem] whitespace-pre-wrap text-sm text-muted-foreground line-clamp-3" dir="auto">
          {text || <span className="italic">No content generated yet</span>}
        </p>

        <div className="mt-auto flex flex-wrap gap-2 pt-1">
          {handOff === 'template' && (
            <Button
              size="sm"
              onClick={onUse}
              disabled={!hasSelection}
              title={hasSelection ? undefined : 'Open it and select the text, photo or video to use first.'}
            >
              <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Use in a template
            </Button>
          )}
          {handOff === 'campaign' && (
            <Button size="sm" onClick={onUse} disabled={starting}>
              {starting
                ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Starting…</>
                : <><Send className="mr-1.5 h-3.5 w-3.5" /> Use in a campaign</>}
            </Button>
          )}
          {canUpdate && (
            <Button size="sm" variant="outline" onClick={onOpen}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" /> Open
            </Button>
          )}
          {canCreate && (
            <Button size="sm" variant="ghost" onClick={onDuplicate}>
              <Copy className="mr-1.5 h-3.5 w-3.5" /> Duplicate
            </Button>
          )}
          {canDelete && (
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
